import { computed, Ref, ref } from 'vue';
import { useDialog } from '@negar/core';
import {
  useDeleteDepositReceiptQuery,
  useDeleteWithdrawalQuery,
} from '@core/cash';
import {
  Deposit,
  EPayment,
  GetBrokerChangeItem,
  WithdrawalItem,
} from '@libs/api-reference';
import { Fn } from '@vueuse/core';

import {
  AccountTurnoverFilterForm,
  DeleteDepositReceiptDialog,
  DeleteWithdrawalFooterDialog,
  DepositForm,
  DepositHistory,
  DepositHistoryItemDialog,
  DepositReceiptHistory,
  DepositReceiptHistoryItemDialog,
  LastWithdrawalTable,
  PayFastDialogContent,
  RegisterReceiptForm,
  WithdrawalForm,
  WithdrawalItemDetailsDialog,
  AddBankAccountDialog,
  AddBankAccountFooterDialog,
  DirectDebitDescription,
  WithdrawalBalanceDialogContent,
  CancelDirectDebitDialog,
} from '@/modules/cash';

import { i18n } from '@/plugins/i18n';

const { t } = i18n.global;

export default function useCashDialogs() {
  const { add: addDialog } = useDialog();

  const openDepositDialog = (totalAmount?: number) => {
    const isDepositExpanded = ref(false);

    addDialog({
      title: t('common.deposit'),
      expandable: true,
      expandableLabel: computed(() =>
        isDepositExpanded.value
          ? `${t('misc.close')} ${t('deposit.recentRequests')}`
          : t('deposit.recentRequests')
      ),
      onExpandedUpdate: (newValue) => (isDepositExpanded.value = newValue),
      content: computed(() => ({
        component: DepositForm,
        props: {
          totalAmount,
        },
      })),
      expandedContent: computed(() => ({
        component: DepositHistory,
        props: {},
      })),
    });
  };

  const openDepositReceiptDeleteDialog = ({
    brokerChangeItem,
    isLoadingDeleteReceipt,
    deleteDepositReceiptButtonRef,
    deleteReceipt,
    removeDeleteDialog,
  }: {
    brokerChangeItem: GetBrokerChangeItem;
    isLoadingDeleteReceipt: Ref<boolean>;
    deleteDepositReceiptButtonRef: HTMLElement | undefined;

    deleteReceipt: ReturnType<
      typeof useDeleteDepositReceiptQuery
    >['deleteReceipt'];
    removeDeleteDialog: Fn;
  }) => {
    addDialog({
      title: t('modules.cash.register_deposit_receipt.cancel_request_title'),
      content: computed(() => ({
        component: DeleteDepositReceiptDialog,
        referenceElementRef: deleteDepositReceiptButtonRef,
        bodyClass: '!w-1/5',
        placement: 'top',
        props: {
          requestId: brokerChangeItem.requestId,
          isDeletingDepositReceiptRequest: isLoadingDeleteReceipt.value,
          onDelete: deleteReceipt,
          onClose: removeDeleteDialog,
        },
      })),
    });
  };

  const openDepositReceiptHistoryInfoDialog = (brokerChangeItem: Deposit) => {
    addDialog({
      title: t('misc.requestDetails'),
      content: computed(() => ({
        component: DepositReceiptHistoryItemDialog,
        props: {
          item: brokerChangeItem,
        },
      })),
    });
  };

  const openDirectDebitDialog = ({
    isActiveDirectDebit,
  }: {
    isActiveDirectDebit: boolean;
  }) => {
    addDialog({
      title: t('deposit.activateDirectDebit'),
      content: computed(() => ({
        component: DirectDebitDescription,
        props: { isActiveDirectDebit },
      })),
    });
  };

  const openCancelDirectDebitDialog = (directDebitId: string) => {
    addDialog({
      title: t('deposit.cancelDirectDebit'),
      bodyClass: '!w-[455px]',
      content: computed(() => ({
        component: CancelDirectDebitDialog,
        props: { directDebitId },
      })),
    });
  };

  const openDepositReceiptDialog = () => {
    const isDepositReceiptExpanded = ref(true);

    addDialog({
      title: t('modules.cash.register_deposit_receipt.title'),
      expandable: true,
      defaultExpanded: isDepositReceiptExpanded.value,
      expandableLabel: computed(() =>
        isDepositReceiptExpanded.value
          ? `${t('misc.close')} ${t('common.recentRequests')}`
          : t('common.recentRequests')
      ),
      onExpandedUpdate: (newValue) =>
        (isDepositReceiptExpanded.value = newValue),
      content: computed(() => ({
        component: RegisterReceiptForm,
        props: {},
      })),
      expandedContent: computed(() => ({
        component: DepositReceiptHistory,
        props: {},
      })),
    });
  };

  const openWithdrawalDialog = () => {
    const isWithdrawExpanded = ref(true);

    addDialog({
      title: t('withdrawal.title'),
      content: computed(() => ({
        component: WithdrawalForm,
        props: {},
      })),
      expandable: true,
      expandableLabel: computed(() =>
        isWithdrawExpanded.value
          ? `${t('misc.close')} ${t('common.recentRequests')}`
          : t('common.recentRequests')
      ),
      defaultExpanded: isWithdrawExpanded.value,
      onExpandedUpdate: (newValue) => {
        isWithdrawExpanded.value = newValue;
      },
      expandedContent: computed(() => ({
        component: LastWithdrawalTable,
        props: {},
      })),
    });
  };

  const openWithdrawalDeleteDialog = ({
    withdrawal,
    isLoadingDeleteWithdrawal,
    deleteWithdrawal,
    deleteWithdrawalButtonRef,
  }: {
    withdrawal: WithdrawalItem;
    isLoadingDeleteWithdrawal: Ref<boolean>;
    deleteWithdrawalButtonRef: HTMLElement | undefined;
    deleteWithdrawal: ReturnType<
      typeof useDeleteWithdrawalQuery
    >['deleteWithdrawal'];
  }) => {
    addDialog({
      title: t('withdrawal.cancelWithdrawalRequest'),
      content: t('withdrawal.deleteConfirmationMessage'),
      referenceElementRef: deleteWithdrawalButtonRef,
      bodyClass: '!w-1/5',
      placement: 'top',
      footer: computed(() => ({
        component: DeleteWithdrawalFooterDialog,
        props: {
          loadingDeleteWithdrawal: isLoadingDeleteWithdrawal.value,
          onDeleteWithdrawal: () => deleteWithdrawal({ withdrawal }),
        },
      })),
    });
  };

  const openPayFastDialog = () => {
    addDialog({
      title: t('withdrawal.payFast'),
      bodyClass: '!w-1/5',
      content: computed(() => ({
        component: PayFastDialogContent,
        props: {},
      })),
    });
  };

  const openWithdrawalBalanceDialog = () => {
    addDialog({
      title: t('withdrawal.withdrawalBalance'),
      bodyClass: '!w-1/4',
      content: computed(() => ({
        component: WithdrawalBalanceDialogContent,
        props: {},
      })),
    });
  };

  const openWithdrawalHistoryInfoDialog = (withdrawal: WithdrawalItem) => {
    addDialog({
      title: t('misc.requestDetails'),
      content: computed(() => ({
        component: WithdrawalItemDetailsDialog,
        props: {
          withdrawal: withdrawal,
        },
      })),
    });
  };

  const openAccountTurnOverFilterFormDialog = () => {
    addDialog({
      title: t('modules.cash.accountTurnoverFilterTitle'),
      content: computed(() => ({
        component: AccountTurnoverFilterForm,
        props: {},
      })),
    });
  };

  const openDepositHistoryInfoDialog = (item: EPayment) => {
    addDialog({
      bodyClass: '!w-1/4',
      title: t('misc.requestDetails'),
      content: computed(() => ({
        component: DepositHistoryItemDialog,
        props: { item },
      })),
    });
  };

  const openAddBankAccountDialog = () => {
    addDialog({
      title: t('withdrawal.createBankAccountTitle'),
      wrapperClass: 'w-full',
      content: computed(() => ({
        component: AddBankAccountDialog,
        props: {},
      })),
      footer: computed(() => ({
        component: AddBankAccountFooterDialog,
        props: {},
      })),
    });
  };

  return {
    openDepositDialog,
    openDepositReceiptDeleteDialog,
    openDepositReceiptHistoryInfoDialog,
    openDepositReceiptDialog,
    openWithdrawalDeleteDialog,
    openWithdrawalDialog,
    openPayFastDialog,
    openWithdrawalBalanceDialog,
    openWithdrawalHistoryInfoDialog,
    openAccountTurnOverFilterFormDialog,
    openDepositHistoryInfoDialog,
    openAddBankAccountDialog,
    openDirectDebitDialog,
    openCancelDirectDebitDialog,
  };
}
